import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import type {
  McpApiKey,
  McpApiKeyListResponse,
  McpCreateApiKeyResponse,
  McpRevokeApiKeyResponse
} from './mcp-api-key.types';

let refreshPromise: Promise<boolean> | null = null;

function getAuthBaseUrl(): string {
  return `${environment.apiUrl.replace('{version}', '2.0')}/auth`;
}

function getRefreshUrl(): string {
  return `${getAuthBaseUrl()}/token/refresh`;
}

async function refreshAccessToken(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = fetch(getRefreshUrl(), {
      method: 'POST',
      credentials: 'include',
    })
      .then((response) => response.ok)
      .catch(() => false)
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

@Injectable({ providedIn: 'root' })
export class McpApiKeyService {
  listMyKeys(companyId: string, userId: string): Promise<McpApiKey[]> {
    return this.request<McpApiKeyListResponse>(
      'GET',
      this.userKeysUrl(companyId, userId),
    ).then((res) => res.data?.keys ?? []);
  }

  /** All connections in the company with the TD user who created each (auth service contract, TMCP-42). */
  listCompanyKeys(companyId: string): Promise<McpApiKey[]> {
    return this.request<McpApiKeyListResponse>(
      'GET',
      `${getAuthBaseUrl()}/api-keys/companies/${encodeURIComponent(companyId)}/keys`,
    ).then((res) => res.data?.keys ?? []);
  }

  createMyKey(companyId: string, userId: string, name: string): Promise<McpCreateApiKeyResponse['data']> {
    return this.request<McpCreateApiKeyResponse>(
      'POST',
      this.userKeysUrl(companyId, userId),
      { name },
    ).then((res) => res.data);
  }

  revokeMyKey(companyId: string, userId: string, keyId: string): Promise<void> {
    return this.request<McpRevokeApiKeyResponse>(
      'DELETE',
      `${this.userKeysUrl(companyId, userId)}/${encodeURIComponent(keyId)}`,
    ).then(() => undefined);
  }

  private userKeysUrl(companyId: string, userId: string): string {
    return `${getAuthBaseUrl()}/api-keys/companies/${encodeURIComponent(companyId)}`
      + `/users/${encodeURIComponent(userId)}/keys`;
  }

  private async request<T>(
    method: 'GET' | 'POST' | 'DELETE',
    url: string,
    body?: Record<string, string>,
  ): Promise<T> {
    const response = await this.fetchWithAuthRetry(method, url, body);
    let payload: T & { error?: string, message?: string };
    try {
      payload = await response.json();
    } catch {
      throw new Error('Unexpected response from server');
    }

    if (!response.ok) {
      const message = payload?.message || payload?.error || `Request failed (${response.status})`;
      throw new Error(message);
    }

    return payload;
  }

  private async fetchWithAuthRetry(
    method: 'GET' | 'POST' | 'DELETE',
    url: string,
    body?: Record<string, string>,
  ): Promise<Response> {
    let response = await this.fetchOnce(method, url, body);
    if (response.status === 401) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        response = await this.fetchOnce(method, url, body);
      }
    }
    return response;
  }

  private fetchOnce(
    method: 'GET' | 'POST' | 'DELETE',
    url: string,
    body?: Record<string, string>,
  ): Promise<Response> {
    return fetch(url, {
      method,
      credentials: 'include',
      headers: body ? { 'content-type': 'application/json', accept: 'application/json' } : { accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
  }
}
